import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const GOOGLE_API_BASE = process.env.REACT_APP_API_URL || '';

// Google "G" logo SVG
function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [form, setForm]           = useState({ email: '', password: '' });
  const [error, setError]         = useState('');
  const [googleOnly, setGoogleOnly] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Read ?error= from URL (set by OAuth callback or server redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errParam = params.get('error');
    if (errParam === 'google_failed') {
      setError('Google sign-in failed. Please try again or use your email and password.');
    } else if (errParam) {
      setError(decodeURIComponent(errParam));
    }
  }, [location.search]);

  // Check if Google OAuth is configured on the server
  useEffect(() => {
    API.get('/auth/google-enabled')
      .then(({ data }) => setGoogleEnabled(data.enabled))
      .catch(() => setGoogleEnabled(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setGoogleOnly(false);
    setLoading(true);
    try {
      const { data } = await API.post('/landlord/login', form);
      login(data.token, { ...data.user, role: 'landlord' });
      navigate('/dashboard');
    } catch (err) {
      const body = err.response?.data;
      if (body?.googleOnly) {
        setGoogleOnly(true);
        setError(body.error || 'This account uses Google sign-in. Please use the button above.');
      } else {
        setError(body?.error || 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    setGoogleLoading(true);
    // Full-page redirect to the OAuth endpoint
    window.location.href = `${GOOGLE_API_BASE}/api/auth/google`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Brand accent */}
      <div className="h-0.5 bg-brand-600 w-full" />

      {/* Minimal nav */}
      <div className="bg-white border-b border-slate-200 px-6 h-14 flex items-center">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-brand-600 rounded-lg flex items-center justify-center shadow-sm">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <span className="font-bold text-slate-800 text-[15px] tracking-tight">
            Rent<span className="text-brand-600">Friendly</span>
          </span>
        </Link>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to your landlord account</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-7">

            {/* Error banner */}
            {error && (
              <div className="mb-5 flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-sm text-red-700">
                  <p>{error}</p>
                  {googleOnly && googleEnabled && (
                    <button
                      onClick={handleGoogleSignIn}
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-red-700 underline hover:text-red-900"
                    >
                      Sign in with Google
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Google button */}
            {googleEnabled && (
              <>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors shadow-sm disabled:opacity-60"
                >
                  {googleLoading ? (
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <GoogleIcon />
                  )}
                  {googleLoading ? 'Redirecting…' : 'Sign in with Google'}
                </button>

                {/* Divider */}
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2.5 text-slate-400">or sign in with email</span>
                  </div>
                </div>
              </>
            )}

            {/* Email / password form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="current-password"
                  placeholder="Your password"
                  className="form-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center mt-1"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>

          <div className="mt-5 space-y-2 text-center">
            <p className="text-sm text-slate-500">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-brand-600 hover:text-brand-700 font-medium">Create one</Link>
            </p>
            <p className="text-sm text-slate-400">
              <Link to="/forgot-password" className="text-slate-500 hover:text-slate-700">Forgot your password?</Link>
            </p>
            <p className="text-sm text-slate-400">
              Are you a tenant?{' '}
              <Link to="/tenant/login" className="text-slate-500 hover:text-slate-700">Tenant sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
